import { useIntervalFn, useThrottleFn } from '@vueuse/core';
import { computed, ref, watch } from 'vue';
import type { Ref } from 'vue';
import { useEventEmitter } from '@/composable/useEventEmitter';
import { RecommendationApi } from '@/core/lib/recommendationApi';
import type { IBetBuilderCombinationSelectionDisplayRule, IBetBuilderRecommendation } from '@/interface/betBuilder';

const LIVE_POLL_INTERVAL_MS = 5_000;
const NON_LIVE_POLL_INTERVAL_MS = 15_000;
const REFRESH_THROTTLE_MS = 5_000;

export function useBetBuilderRecommendationsByMatch(matchId: number, isLive: Ref<boolean>, isEnabled: Ref<boolean>, displayRule: Ref<IBetBuilderCombinationSelectionDisplayRule>) {
    const recommendations = ref<IBetBuilderRecommendation[]>([]);
    const isLoaded = ref(false);

    async function fetchRecommendation() {
        try {
            recommendations.value = await RecommendationApi.getBetBuilderRecommendationsByMatch(matchId, displayRule.value);
        } catch (error) {
            console.error('Failed to fetch bet builder recommendation by match', error);
            recommendations.value = [];
        }
        isLoaded.value = true;
    }

    const pollIntervalMs = computed(() => (isLive.value ? LIVE_POLL_INTERVAL_MS : NON_LIVE_POLL_INTERVAL_MS));
    const { pause, resume } = useIntervalFn(fetchRecommendation, pollIntervalMs, { immediate: false });

    watch(isEnabled, (enabled) => {
        if (enabled) {
            fetchRecommendation();
            resume();
        } else {
            pause();
            recommendations.value = [];
            isLoaded.value = false;
        }
    }, { immediate: true });

    const throttledFetchRecommendation = useThrottleFn(fetchRecommendation, REFRESH_THROTTLE_MS, false, true);
    const { on } = useEventEmitter();
    on('oddsDisplay:refreshBetBuilderRecommendation', (refreshMatchId: number | null) => {
        if (!isEnabled.value) return;
        if (refreshMatchId === null || refreshMatchId === matchId) {
            throttledFetchRecommendation();
        }
    });

    const hasRecommendations = computed(() => recommendations.value.length > 0);

    return {
        recommendations,
        hasRecommendations,
        isLoaded,
    };
}
